import { useState, useEffect } from "react";
import axios from "axios";
import { BookOpen, Eye, Sparkles, Loader2, Hotel, Calendar, Plus, User, Copy, Globe, Users, UserCheck, MapPin, Moon, BedDouble, Baby, Star, Utensils, Phone, Mail, FileText, Clock, Trash2, MessageCircle, ExternalLink, Search, ChevronDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const AdminQuotes = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("agents");
  const [quotes, setQuotes] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("this_month");
  
  // Create Quote State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    agent_id: "",
    trip_type: "domestic",
    destination: "",
    covered_cities: "",
    pickup_location: "",
    drop_location: "",
    travel_date: "",
    return_date: "",
    pax_adults: 2,
    pax_children: 0,
    pax_infants: 0,
    no_of_rooms: 1,
    cwb: 0,
    cnb: 0,
    budget: "",
    hotel_category: "3-star",
    requirements: ""
  });

  useEffect(() => {
    fetchData();
    fetchAgents();
    fetchEmployees();
  }, []);

  // Auto-refresh (~1s) — new quotes submitted by agents appear without manual reload
  useAutoRefresh(() => fetchData());

  const fetchData = async () => {
    try {
      const [quotesRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQuotes(quotesRes.data);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/admin/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only active employees
      const activeEmployees = response.data.filter(e => e.status === 'approved' || e.status === 'active');
      setEmployees(activeEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleAssignQuote = async (quoteId, userId) => {
    try {
      const response = await axios.put(
        `${API}/quotes/${quoteId}/assign`,
        { assigned_to: userId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(userId ? "Quote assigned successfully!" : "Assignment removed");
      fetchData(); // Refresh quotes
    } catch (error) {
      console.error("Error assigning quote:", error);
      toast.error("Failed to assign quote");
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotes`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote created successfully!");
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create quote");
    }
  };

  const handleDuplicateQuote = async (quote) => {
    if (!window.confirm("Duplicate this quote? You'll be taken to the editor to update enquiry details + activities.")) return;
    try {
      const res = await axios.post(`${API}/quotes/${quote.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Quote duplicated as ${res.data.quote_number}`);
      // Jump straight into the QuoteBuilder for the new copy so admin can edit
      navigate(`/admin/quote-builder/${res.data.id}`);
    } catch (error) {
      toast.error("Failed to duplicate quote");
    }
  };

  const convertInquiryToQuote = async (inquiry) => {
    setCreateForm({
        ...createForm,
        destination: inquiry.destination || "",
        requirements: inquiry.message || "",
        agent_id: ""
    });
    setShowCreateModal(true);
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Enquiry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete enquiry");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-100 text-amber-700";
      case "quoted": return "bg-emerald-100 text-emerald-700";
      case "approved": return "bg-emerald-100 text-emerald-700";
      case "rejected": return "bg-red-100 text-red-700";
      case "booked": return "bg-blue-100 text-blue-700";
      case "revision_requested": return "bg-orange-100 text-orange-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getMealPlanLabel = (plan) => {
    const plans = { ep: "EP (Room Only)", cp: "CP (Breakfast)", map: "MAP (Breakfast + Dinner)", ap: "AP (All Meals)", ai: "All Inclusive" };
    return plans[plan] || plan || "-";
  };

  const getHotelCategoryLabel = (cat) => {
    const cats = { budget: "Budget", "3-star": "3-Star", "4-star": "4-Star", "5-star": "5-Star", luxury: "Luxury Resort" };
    return cats[cat] || cat || "-";
  };

  const getPeriodBounds = (period) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (period === "all") return null;
    if (period === "this_month") return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0, 23, 59, 59) };
    if (period === "last_month") return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0, 23, 59, 59) };
    // Indian FY quarters: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
    if (period === "q1") return { start: new Date(y, 3, 1), end: new Date(y, 5, 30, 23, 59, 59) };
    if (period === "q2") return { start: new Date(y, 6, 1), end: new Date(y, 8, 30, 23, 59, 59) };
    if (period === "q3") return { start: new Date(y, 9, 1), end: new Date(y, 11, 31, 23, 59, 59) };
    if (period === "q4") return { start: new Date(y, 0, 1), end: new Date(y, 2, 31, 23, 59, 59) };
    if (period === "this_year") return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59) };
    if (period === "last_year") return { start: new Date(y - 1, 0, 1), end: new Date(y - 1, 11, 31, 23, 59, 59) };
    return null;
  };

  // Period-filtered quotes (for tab counts)
  const periodFilteredQuotes = quotes.filter((q) => {
    const bounds = getPeriodBounds(periodFilter);
    if (bounds) {
      const created = q.created_at ? new Date(q.created_at) : null;
      if (!created || created < bounds.start || created > bounds.end) return false;
    }
    return true;
  });

  // Fully filtered (period + status + search)
  const filteredQuotes = periodFilteredQuotes.filter((q) => {
    if (statusFilter === "pending" && q.status !== "pending") return false;
    if (statusFilter === "sent" && !["quoted", "approved"].includes(q.status)) return false;
    if (statusFilter === "booked" && q.status !== "booked") return false;
    if (searchQuery) {
      const lq = searchQuery.toLowerCase();
      const haystack = [q.quote_number, q.agent_name, q.agent_company, q.destination, q.client_name].join(" ").toLowerCase();
      if (!haystack.includes(lq)) return false;
    }
    return true;
  });

  const conversionPct = periodFilteredQuotes.length > 0
    ? Math.round((periodFilteredQuotes.filter(q => q.status === "booked").length / periodFilteredQuotes.length) * 100)
    : 0;

  const periodLabels = {
    all: "All Time", this_month: "This Month", last_month: "Last Month",
    q1: "Q1 (Apr–Jun)", q2: "Q2 (Jul–Sep)", q3: "Q3 (Oct–Dec)", q4: "Q4 (Jan–Mar)",
    this_year: "This Year", last_year: "Last Year"
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-quotes">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">Quote Management</h1>
            </div>
            <p className="text-gray-500">Manage inquiries from Agents and Website</p>
          </div>
          <Button onClick={() => navigate('/admin/request-quote')} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="admin-create-quote-btn">
            <Plus className="w-4 h-4" /> Create Quote
          </Button>
        </div>

        {/* Status Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
          {/* Left: Status Tabs */}
          <div className="flex items-center gap-1">
            {[
              { key: "all", label: `All Quotes (${periodFilteredQuotes.length})` },
              { key: "pending", label: `Pending (${periodFilteredQuotes.filter(q => q.status === "pending").length})` },
              { key: "sent", label: `Sent (${periodFilteredQuotes.filter(q => ["quoted","approved"].includes(q.status)).length})` },
              { key: "booked", label: `Booked (${periodFilteredQuotes.filter(q => q.status === "booked").length})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                data-testid={`status-filter-${key}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  statusFilter === key
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
            <span className="ml-3 px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-50 text-indigo-700 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Conversion: <strong>{conversionPct}%</strong>
            </span>
          </div>
          {/* Right: Period + Search */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                data-testid="period-filter-dropdown"
                className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium focus:outline-none focus:ring-1 focus:ring-emerald-400 cursor-pointer"
              >
                {Object.entries(periodLabels).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search quotes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="quote-search-input"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-400 w-44"
              />
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-gray-50">
                <TabsTrigger value="agents" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white w-32">
                    <Users className="w-4 h-4 mr-2" /> Agents
                </TabsTrigger>
                <TabsTrigger value="web" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white w-32">
                    <Globe className="w-4 h-4 mr-2" /> Web
                </TabsTrigger>
            </TabsList>

            <TabsContent value="agents">
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Quote #</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Agent</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Guest</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Destination</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Travel Date</th>
                                        <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Amount</th>
                                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Assign To</th>
                                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredQuotes.map((quote) => (
                                        <tr key={quote.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-4 py-3">
                                                <span className="font-mono text-emerald-600 text-xs font-medium" data-testid={`admin-quote-number-${quote.id}`}>#{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}</span>
                                            </td>
                                            {/* Agent: Company / Name / Phone */}
                                            <td className="px-4 py-3">
                                                <p className="font-semibold text-gray-800 text-sm">{quote.agent_company || '-'}</p>
                                                <p className="text-gray-500 text-xs">{quote.agent_name || '-'}</p>
                                                {quote.agent_phone && (
                                                    <p className="text-gray-400 text-xs flex items-center gap-0.5 mt-0.5">
                                                        <Phone className="w-3 h-3" />{quote.agent_phone}
                                                    </p>
                                                )}
                                            </td>
                                            {/* Guest: Name / Pax breakdown */}
                                            <td className="px-4 py-3">
                                                <p className="text-gray-800 text-sm font-medium">{quote.client_name || <span className="text-gray-400 italic text-xs">No guest</span>}</p>
                                                <p className="text-gray-500 text-xs mt-0.5">
                                                    {quote.pax_adults || 0} Adults
                                                    {(quote.cwb > 0) ? ` + ${quote.cwb} CWB` : ""}
                                                    {(quote.cnb > 0) ? ` + ${quote.cnb} CNB` : ""}
                                                </p>
                                            </td>
                                            {/* Destination + Nights */}
                                            <td className="px-4 py-3">
                                                <p className="text-gray-700 text-sm font-medium">{quote.destination || '-'}</p>
                                                {(() => {
                                                    const nights = quote.nights || (
                                                        quote.travel_date && quote.return_date
                                                            ? Math.ceil((new Date(quote.return_date) - new Date(quote.travel_date)) / 86400000)
                                                            : null
                                                    );
                                                    return nights > 0 ? <p className="text-xs text-gray-400">{nights} Nights</p> : null;
                                                })()}
                                            </td>
                                            {/* Travel Date + Return Date */}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <p className="text-gray-700 text-sm">{formatDate(quote.travel_date)}</p>
                                                {quote.return_date && (
                                                    <p className="text-xs text-gray-400">→ {formatDate(quote.return_date)}</p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium text-gray-800">
                                                {quote.quote_amount ? `₹${Number(quote.quote_amount).toLocaleString('en-IN')}` : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <Badge className={`text-xs capitalize ${getStatusColor(quote.status)}`}>{quote.status}</Badge>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Select
                                                    value={quote.assigned_to || "unassigned"}
                                                    onValueChange={(value) => handleAssignQuote(quote.id, value === "unassigned" ? null : value)}
                                                >
                                                    <SelectTrigger className={`w-36 h-9 text-sm ${quote.assigned_to ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                                        <SelectValue placeholder="Not Assigned" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="unassigned">
                                                            <span className="text-gray-400">Not Assigned</span>
                                                        </SelectItem>
                                                        {employees.map(emp => (
                                                            <SelectItem key={emp.id} value={emp.id}>
                                                                <div className="flex items-center gap-2">
                                                                    <UserCheck className="w-3 h-3 text-emerald-500" />
                                                                    {emp.name}
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="px-4 py-4 align-top">
                                                <div className="flex gap-1 flex-wrap">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => setSelectedEnquiry(quote)}
                                                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1"
                                                        data-testid={`view-enquiry-${quote.id}`}
                                                    >
                                                        <Eye className="w-4 h-4" /> View
                                                    </Button>
                                                    {["quoted", "approved", "booked"].includes(quote.status) && (
                                                        <Button 
                                                          variant="ghost" 
                                                          size="sm" 
                                                          onClick={() => navigate(`/admin/quote-preview/${quote.id}`)}
                                                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 gap-1"
                                                          data-testid={`preview-quote-${quote.id}`}
                                                        >
                                                          <FileText className="w-4 h-4" /> Preview
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => navigate(`/admin/quote-builder/${quote.id}`)}
                                                        className="text-blue-400 hover:text-blue-300"
                                                    >
                                                        Build Quote
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDuplicateQuote(quote)} className="text-gray-500">
                                                        <Copy className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => deleteQuote(quote.id)}
                                                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                                        data-testid={`delete-quote-${quote.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="web">
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Name</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Contact</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Requirement</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Date</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {inquiries.map((inquiry) => (
                                        <tr key={inquiry.id} className="hover:bg-gray-50/30">
                                            <td className="px-6 py-4 text-gray-800">{inquiry.name}</td>
                                            <td className="px-6 py-4">
                                                <p className="text-gray-600 text-sm">{inquiry.email}</p>
                                                <p className="text-gray-400 text-xs">{inquiry.phone}</p>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 text-sm truncate max-w-[200px]">
                                                {inquiry.destination} - {inquiry.message}
                                            </td>
                                            <td className="px-6 py-4 text-gray-400 text-sm">
                                                {formatDate(inquiry.created_at)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge variant="outline" className="text-gray-500 border-gray-200 capitalize">{inquiry.status}</Badge>
                                            </td>
                                            <td className="px-6 py-4 flex gap-1">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => convertInquiryToQuote(inquiry)}
                                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                >
                                                    Convert
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => deleteInquiry(inquiry.id)}
                                                    className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                                    data-testid={`delete-inquiry-${inquiry.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </TabsContent>
        </Tabs>

        {/* View Enquiry Detail Dialog */}
        <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
          <DialogContent className="max-w-3xl bg-white border-gray-200 text-gray-800 max-h-[90vh] overflow-y-auto" data-testid="enquiry-detail-modal">
            {selectedEnquiry && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <span className="text-lg font-bold text-gray-900">Agent Enquiry Details</span>
                      <p className="text-sm font-normal text-gray-500 mt-0.5">
                        #{selectedEnquiry.quote_number || selectedEnquiry.id.slice(0, 8).toUpperCase()} &bull; Submitted {formatDate(selectedEnquiry.created_at)}
                      </p>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                  {/* Agent Info */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Agent Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-400">Agent Name</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.agent_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Company</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.agent_company || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Status</p>
                        <Badge className={`capitalize ${getStatusColor(selectedEnquiry.status)}`}>{selectedEnquiry.status}</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Trip Details */}
                  <div className="p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
                    <h4 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> Trip Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-400">Trip Type</p>
                        <p className="text-sm font-medium text-gray-800 capitalize">{selectedEnquiry.trip_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Destination</p>
                        <p className="text-sm font-semibold text-emerald-700">{selectedEnquiry.destination || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Covered Cities</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.covered_cities || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Pickup Location</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.pickup_location || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Drop Location</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.drop_location || '-'}</p>
                      </div>
                      {selectedEnquiry.trip_type === 'international' && (
                        <div>
                          <p className="text-xs text-gray-400">Hotel Requirement</p>
                          <Badge className={selectedEnquiry.with_hotel === 'no' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                            {selectedEnquiry.with_hotel === 'no' ? 'Without Hotel' : 'With Hotel'}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dates & Nights */}
                  <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                    <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" /> Travel Schedule
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-400">Travel Date</p>
                        <p className="text-sm font-medium text-gray-800">{formatDate(selectedEnquiry.travel_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Return Date</p>
                        <p className="text-sm font-medium text-gray-800">{formatDate(selectedEnquiry.return_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Nights</p>
                        <div className="flex items-center gap-1.5">
                          <Moon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-bold text-blue-700">
                            {selectedEnquiry.travel_date && selectedEnquiry.return_date
                              ? Math.max(0, Math.ceil((new Date(selectedEnquiry.return_date) - new Date(selectedEnquiry.travel_date)) / (1000*60*60*24)))
                              : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Guests & Rooms */}
                  <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                    <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Guests & Rooms
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-purple-100 text-center">
                        <p className="text-2xl font-bold text-gray-800">{selectedEnquiry.pax_adults || 0}</p>
                        <p className="text-xs text-gray-500">Adults</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-green-100 text-center">
                        <p className="text-2xl font-bold text-green-700">{selectedEnquiry.cwb || 0}</p>
                        <p className="text-xs text-gray-500">CWB</p>
                        {selectedEnquiry.cwb_ages && <p className="text-xs text-green-600 mt-1">Ages: {selectedEnquiry.cwb_ages}</p>}
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                        <p className="text-2xl font-bold text-blue-700">{selectedEnquiry.cnb || 0}</p>
                        <p className="text-xs text-gray-500">CNB</p>
                        {selectedEnquiry.cnb_ages && <p className="text-xs text-blue-600 mt-1">Ages: {selectedEnquiry.cnb_ages}</p>}
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                        <p className="text-2xl font-bold text-amber-700">{selectedEnquiry.pax_infants || 0}</p>
                        <p className="text-xs text-gray-500">Infants</p>
                        {selectedEnquiry.infant_ages && <p className="text-xs text-amber-600 mt-1">Ages: {selectedEnquiry.infant_ages}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-400">No. of Rooms</p>
                        <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                          <BedDouble className="w-4 h-4 text-purple-500" /> {selectedEnquiry.no_of_rooms || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Total Children</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.pax_children || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hotel & Budget Preferences */}
                  <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-100">
                    <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Hotel className="w-3.5 h-3.5" /> Preferences
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-400">Hotel Category</p>
                        <p className="text-sm font-medium text-gray-800 flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-500" />
                          {getHotelCategoryLabel(selectedEnquiry.hotel_category)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Meal Plan</p>
                        <p className="text-sm font-medium text-gray-800 flex items-center gap-1">
                          <Utensils className="w-3.5 h-3.5 text-amber-500" />
                          {getMealPlanLabel(selectedEnquiry.meal_plan)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Hotel Options</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.hotel_options_count || 1} option(s)</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Budget</p>
                        <p className="text-sm font-medium text-gray-800">{selectedEnquiry.budget || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Guest Name */}
                  {selectedEnquiry.client_name && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" /> Guest Information
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-400">Guest Name</p>
                          <p className="text-sm font-medium text-gray-800">{selectedEnquiry.client_name || '-'}</p>
                        </div>
                        {selectedEnquiry.client_phone && (
                          <div>
                            <p className="text-xs text-gray-400">Phone</p>
                            <a href={`tel:${selectedEnquiry.client_phone}`} className="text-sm font-medium text-blue-600 flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" /> {selectedEnquiry.client_phone}
                            </a>
                          </div>
                        )}
                        {selectedEnquiry.client_email && (
                          <div>
                            <p className="text-xs text-gray-400">Email</p>
                            <a href={`mailto:${selectedEnquiry.client_email}`} className="text-sm font-medium text-blue-600 flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" /> {selectedEnquiry.client_email}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Assigned To */}
                  {selectedEnquiry.assigned_to_name && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100 flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="text-xs text-gray-400">Assigned To</p>
                        <p className="text-sm font-semibold text-indigo-700">{selectedEnquiry.assigned_to_name}</p>
                      </div>
                    </div>
                  )}

                  {/* Special Requirements */}
                  {selectedEnquiry.requirements && (
                    <div className="p-4 bg-red-50/50 rounded-lg border border-red-100">
                      <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Special Requirements
                      </h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedEnquiry.requirements}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => { setSelectedEnquiry(null); navigate(`/admin/quote-builder/${selectedEnquiry.id}`); }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                      data-testid="enquiry-build-quote-btn"
                    >
                      <Sparkles className="w-4 h-4" /> Build Quote
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedEnquiry(null)}
                      className="border-gray-300 text-gray-600"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Quote Dialog */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogContent className="max-w-2xl bg-white border-gray-200 text-gray-800">
                <DialogHeader><DialogTitle>Create New Quote</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateQuote} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    <div>
                        <Label>Select Agent</Label>
                        <Select value={createForm.agent_id || "direct"} onValueChange={(v) => setCreateForm({...createForm, agent_id: v === "direct" ? "" : v})}>
                            <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Direct Client / Select Agent" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="direct">Direct Client</SelectItem>
                                {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Destination</Label><Input value={createForm.destination} onChange={e => setCreateForm({...createForm, destination: e.target.value})} className="bg-gray-50 border-gray-200" required /></div>
                        <div><Label>Cities</Label><Input value={createForm.covered_cities} onChange={e => setCreateForm({...createForm, covered_cities: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Travel Date</Label><Input type="date" value={createForm.travel_date} onChange={e => setCreateForm({...createForm, travel_date: e.target.value})} className="bg-gray-50 border-gray-200" required /></div>
                        <div><Label>Return Date</Label><Input type="date" value={createForm.return_date} onChange={e => setCreateForm({...createForm, return_date: e.target.value})} className="bg-gray-50 border-gray-200" required /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div><Label>Adults</Label><Input type="number" value={createForm.pax_adults} onChange={e => setCreateForm({...createForm, pax_adults: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div><Label>Children</Label><Input type="number" value={createForm.pax_children} onChange={e => setCreateForm({...createForm, pax_children: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div><Label>Infants</Label><Input type="number" value={createForm.pax_infants} onChange={e => setCreateForm({...createForm, pax_infants: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                    </div>
                    <div><Label>Requirements</Label><Textarea value={createForm.requirements} onChange={e => setCreateForm({...createForm, requirements: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                    <Button type="submit" className="w-full bg-primary-700">Create & Build</Button>
                </form>
            </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminQuotes;
